/**
 * Advisory Types
 *
 * Document model, channel-state contract and stage contract for the
 * Strategic Advisory feature. Types live alongside the slice rather than a
 * separate `src/models/Advisory.ts`.
 *
 * `successCriteria` / `outOfScope` are per-question fields the backend
 * stores but keeps off the SSE wire — they are not engagement-level fields
 * and there is no separate criteria document they live on. See
 * `advisoryTypes.typecheck.ts` for the compile-failure fixture that proves
 * this shape holds.
 */

/**
 * Project status for the legacy goal journey and the unified review journey.
 * Unified drafts use `review` instead of the legacy extraction statuses.
 */
export type GoalDocStatus =
  | 'review'
  | 'extracting'
  | 'awaiting_confirmation'
  | 'confirmed'
  | 'failed';

/**
 * One user-submitted or refined attempt at the strategic goal.
 *
 * The real field is `goal`, not `text`. Verified against
 * `agent-svc origin/development@afffb5d`,
 * `app/agents/supervisor/advisory_state.py`'s own module docstring —
 * `goal: [{goal, rationale, clarificationNeeded, clarificationQuestion,
 * userFeedback, createdAt}]` — and confirmed in code:
 * `AdvisoryState.render_prior_context` reads `attempt.get('goal', '')`,
 * never `attempt.get('text', ...)`. No document this backend has ever
 * written carries a `text` field here.
 */
export interface GoalAttempt {
  goal: string;
  /** ISO timestamp string. */
  createdAt: string;
}

/**
 * The goal document's `engagement` sub-object carries its OWN status,
 * deliberately distinct strings from the top-level `GoalDocStatus` above.
 * Verified against `agent-svc origin/development@afffb5d`,
 * `advisory_state.py::AdvisoryState.to_doc()` — `classifying` while the
 * confirm-turn's classification step is still running server-side,
 * `classified` once it has produced a real result, `engagement_failed`
 * when it has not (`mark_engagement_failed`).
 */
export type EngagementDocStatus = 'classifying' | 'classified' | 'engagement_failed';

/** One entry of the persisted engagement sub-object's `alternatives` array. Mirrors `SupervisorEngagementAlternative` (`useSupervisorTurn.ts`) field-for-field — kept as a separate type so this module never imports from a hook. */
export interface EngagementAlternative {
  engagementId: string;
  name: string;
  reason: string;
}

/**
 * The goal document's `engagement` sub-object — used for reload recovery.
 * Only consumed for a reload with no live SSE turn in this browser tab;
 * the live-turn path renders from `useSupervisorTurn`'s own
 * `SupervisorEngagementState` and never reads this type.
 *
 * Deliberately carries NO `error` field: `to_doc()` also writes
 * `engagement.error`, the same class of raw provider text the
 * `advisory:errors.*` mapping exists to keep off every other rendered
 * surface (ASVS V7) — this type gives no caller a field to accidentally
 * render it from. Deliberately carries NO `classifiedAt` either — nothing
 * currently renders a timestamp for this sub-object.
 */
export interface EngagementDoc {
  status: EngagementDocStatus;
  engagementId: string;
  name: string;
  description: string;
  confidence: string;
  rationale: string;
  alternatives: EngagementAlternative[];
}

/**
 * `users/{uid}/strategic_advisory/{projectId}` — the goal document.
 *
 * `goal` is an array field of attempts on that document, not a
 * subcollection, and never a single string. `maxIterations` is read from
 * this document and must never be hardcoded to 3 or 5 anywhere in the
 * client.
 *
 * `canRefine` is NOT a field on this document — the backend writes it to no
 * document, only to the SSE `goal` event. `mapGoalDoc` derives it from the
 * server's own expression, `goal.length < maxIterations`, so this interface
 * still carries it as a boolean; it is simply computed rather than read.
 *
 * `engagement` is optional rather than a required `EngagementDoc | null` —
 * dozens of pre-existing `StrategicAdvisoryDoc` literals across this
 * feature's own test suite predate this field and never set it; requiring
 * it would force an unrelated edit onto every one of them for a field only
 * the reload-recovery path reads. `mapGoalDoc` always sets it explicitly
 * (to `null` when absent or malformed), so real documents this mapper
 * produces never rely on the optionality — only hand-built test fixtures
 * do.
 *
 * `analysis` is optional for exactly the same reason and on the same terms.
 */
export interface StrategicAdvisoryDoc {
  reviewWorkflowVersion?: 1;
  workspaceVersion?: 1;
  status: GoalDocStatus;
  goal: GoalAttempt[];
  maxIterations: number;
  canRefine: boolean;
  updatedAt: string | null;
  /** The user's original request, kept as the Brief's project label. */
  originalQuery?: string;
  /** The confirmed goal, kept separately from the iterative goal attempts. */
  confirmedGoal?: string;
  engagement?: EngagementDoc | null;
  analysis?: AnalysisDoc | null;
  answers?: AdvisoryAnswersRunDoc | null;
}

/**
 * `strategic_advisory/{projectId}/facts/company` — the subject of the engagement.
 *
 * The **only** place the company is stored. There is deliberately no mirror on
 * the project document, so there is no pair of copies to drift apart and no
 * reader that has to know which one is authoritative.
 *
 * Read from the user's opening question on the first turn (S-02a) with
 * `confirmed: false`, then confirmed by them on the very next screen — before
 * the goal review, before the fork, and long before any document is uploaded.
 *
 * `error` is non-empty when the read itself failed, which is a different problem
 * for the user than "your question named nobody" and wants different words.
 *
 * Not part of `StrategicAdvisoryDoc`: it lives in a subcollection, so the goal
 * channel's listener never sees it. The engagement page fetches it and passes it
 * to `deriveAdvisoryStage`.
 */
export interface CompanyFact {
  companyName: string;
  confirmed: boolean;
  /** Non-empty when the read failed, as opposed to finding nobody. */
  error: string;
}

/**
 * `strategic_advisory/{projectId}/facts/counterparties` — the OTHER companies.
 *
 * The competitors to be analysed, or the acquisition targets being evaluated.
 * Exists for exactly two of the twenty-one engagement types, because only those
 * two are per-counterparty: `competitor-analysis` answers "right to win"
 * against someone, `investment-m-and-a-decision` answers "valuation synergies"
 * with something. `null` for the other nineteen, and for a project that
 * predates the stage.
 *
 * `kind` is the server's own word for what these are, so the UI can call an
 * acquisition target an acquisition target rather than a competitor.
 */
export interface CounterpartiesFact {
  companies: string[];
  kind: string;
  confirmed: boolean;
  /** Non-empty when the read failed, as opposed to the user naming nobody. */
  error: string;
  /** Keyed by company name, exactly as `companies` spells it. */
  profiles: Record<string, CounterpartyProfile>;
  /** Companies removed from the engagement for want of obtainable evidence. */
  dropped: DroppedCounterparty[];
}

/**
 * Live counterparty-research progress, as THIS tab's stream reported it.
 *
 * Exists because `facts/counterparties` is read once, at mount — before any
 * profile exists. The research is fired by the confirm turn and writes its
 * records over the following minutes, so a stage derived from the fetched
 * document alone can never show a run in progress: the user confirms the list
 * and lands on a screen with no rows, no progress and no retry, which is
 * exactly what a hang looks like.
 *
 * Firestore stays authoritative for the terminal statuses — this is the live
 * overlay, reconciled against a re-read when the stage completes.
 */
export interface LiveCounterpartyProfiles {
  kind: string;
  /** Every company the run was planned for, from `counterpartyProfilesStarted`. */
  companies: string[];
  /** In flight right now, in the order they started. */
  started: string[];
  /** Terminal outcomes, keyed by company — success AND failure. */
  settled: Record<string, LiveCounterpartyOutcome>;
  /** Companies removed from the engagement mid-run. */
  dropped: string[];
  /** Set once the whole stage finishes; `null` while it runs. */
  completed: { planned: number; ingested: number } | null;
}

/** One company's terminal outcome off `counterpartyProfileSettled`. */
export interface LiveCounterpartyOutcome {
  status: CounterpartyProfileStatus;
  fileName: string;
  chars: number;
  detail: string;
}

/**
 * A counterparty the research could not find anything about, and that has
 * therefore been removed from the engagement.
 *
 * Written by agent-svc once the escalation is exhausted: the first attempt
 * failed, the user supplied the URL they had, and that failed too. At that point
 * no further attempt has new information to work with.
 *
 * This is the RECEIPT, never a profile. It exists because the research is
 * detached server-side and can exhaust itself minutes after the tab was closed,
 * so the stream cannot be relied on to tell the user. Without it the confirmed
 * list would simply be shorter on the next read, with nothing saying which
 * company left or why — indistinguishable from a bug.
 */
export interface DroppedCounterparty {
  company: string;
  /** A short server code, e.g. `missing_evidence`. Never provider prose. */
  reason: string;
  /** The URL the user offered before it was given up on, if any. */
  sourceUrl: string;
  /** The last research status it reached, e.g. `research_failed`. */
  lastStatus: string;
  droppedAt: string;
}

/**
 * One researched counterparty profile, off `facts/counterparties.profiles`.
 *
 * Written by agent-svc as the research runs, so a client that reconnects
 * mid-run reads the current state rather than waiting for a stream. `profile`
 * is the prose itself — stored as well as ingested, because docintel is a
 * retrieval index and not something a client can read from.
 */
export interface CounterpartyProfile {
  company: string;
  status: CounterpartyProfileStatus;
  fileName: string;
  chars: number;
  /** The prose, capped server-side at 20k chars. */
  profile: string;
  /** Why it failed, when it did. Never provider prose — a short server code. */
  detail: string;
  /** The URL the user supplied to seed a retry, if any. */
  sourceUrl: string;
}

/**
 * `profiles[company].status`.
 *
 * `researching` is written BEFORE the job starts, so an absent entry means the
 * run has not reached that company — which is not the same as in-flight.
 *
 * Four of the five are terminal, and only `ingested` is a success. The
 * distinction between them decides what a retry does:
 *
 * * `ingest_failed` / `ingest_unavailable` — the research SUCCEEDED and the
 *   prose is stored; a retry re-ingests it and pays for nothing new.
 * * `research_failed` — nothing to salvage; a retry runs a fresh job, and is
 *   where a user-supplied URL earns its keep.
 * * `too_thin` — research returned something, but too little to be worth
 *   putting in the corpus, where it would read as evidence.
 */
export type CounterpartyProfileStatus =
  | 'researching'
  | 'ingested'
  | 'research_failed'
  | 'too_thin'
  | 'ingest_failed'
  | 'ingest_unavailable';

/** Every status that means the research is FINISHED, successfully or not. */
export const TERMINAL_COUNTERPARTY_PROFILE_STATUSES: readonly CounterpartyProfileStatus[] = [
  'ingested',
  'research_failed',
  'too_thin',
  'ingest_failed',
  'ingest_unavailable',
];

export type AdvisoryAnswersDocStatus =
  | 'answering'
  | 'answers_running'
  | 'answers_completed'
  | 'answers_partial'
  | 'answers_failed';

/** Durable S-06 summary stored on the project document. */
export interface AdvisoryAnswersRunDoc {
  status: AdvisoryAnswersDocStatus;
  runId: string;
  rerunMode: 'resume' | 'retryGaps' | 'fresh' | '';
  plannedItems: number;
  answeredItems: number;
  partialItems: number;
  unanswerableItems: number;
  failedItems: number;
  startedAt: string | null;
  completedAt: string | null;
}

/**
 * `analysis.status` — the S-05 run's lifecycle on the project document.
 *
 * `partial` is terminal and deliberately NOT a failure: an engagement where one
 * branch died still answered the others, and reporting that as failed would throw
 * away work the user paid for. The backend literal is `analysis_failed`, not
 * `failed`, so it cannot be confused with the goal document's own top-level
 * `status: 'failed'` — those are different situations and the wire keeps them
 * distinct.
 */
export type AnalysisDocStatus =
  | 'prepared'
  | 'running'
  | 'completed'
  | 'partial'
  | 'analysis_failed';

/**
 * The `analysis` sub-object of `users/{uid}/strategic_advisory/{projectId}`.
 *
 * One slot, never a list: a project runs one engagement, and the per-attempt
 * detail lives in the `tracking/{runId}` subcollection where it can grow without
 * bound. `runId` is the pointer into it.
 *
 * This is the ONLY reload-survival path for a run in flight. An engagement is
 * roughly thirteen minutes on the debug shape and hours on a full one, so a page
 * reload mid-run is the normal case rather than the edge case — the SSE stream is
 * gone by then and this document is what says a run is still going.
 *
 * `conversationId` is a fact about the project rather than about a run: both
 * docintel collection handles are `uuid5(namespace, conversationId + suffix)`, so
 * it is minted once and never re-minted. Re-minting would leave every uploaded
 * document in a collection nothing reads.
 *
 * `error` is carried on the wire but deliberately NOT on this type — it is raw
 * provider text, and this client never renders that (ASVS V7). Whether the run
 * failed is answerable from `status` alone.
 */
export interface AnalysisDoc {
  status: AnalysisDocStatus;
  runId: string;
  conversationId: string;
  /** Where the user's documents are uploaded. Derived from `conversationId`. */
  docsCollectionId: string;
  /** Where pgms ingests each completed node's output. Reported, never written by a client. */
  internalCollectionId: string;
  /** The root turn's id, which IS the conversation id once the run has opened. */
  rootTurnId: string;
  nodeProfile: string;
  debug: boolean;
  plannedTurns: number;
  completedTurns: number;
  failedTurns: number;
  startedAt: string | null;
  completedAt: string | null;
}

/**
 * One document in the `strategic_advisory/{projectId}/keyQuestions`
 * subcollection.
 *
 * This type carries NO `successCriteria` and NO `outOfScope` field. The SSE
 * `questions` event carries eleven of the fifteen stored key-question
 * fields and deliberately omits `inScope`, `outOfScope`, `successCriteria`
 * and `createdAt` to keep the event small — those three heavy fields are
 * stored server-side but never cross the wire. `successCriteria` in
 * particular is the agent's own internal grounding bar for that question
 * (`models.py` calls it "the grounding bar as one prompt-ready block"),
 * agent-internal and out of scope by product decision; it is never read or
 * edited by this client. There is no separate engagement-level criteria
 * document for these fields to live on — none has ever existed.
 *
 * The real fields are `question` and `subquestions` (lowercase q), not
 * `text`/`subQuestions`. Verified against both the SSE `questions` event's
 * own field names and the stored document, corroborated by `agent-svc`'s
 * `service.py` docstring — no field named `text` or `subQuestions` exists
 * on the wire or the document.
 *
 * `order`: the server's topological sort of the engagement's dependency
 * graph, computed once at instantiate time ("the answer order,
 * topologically sorted from `dependsOn` at instantiate time... render in
 * this order; never sort client-side by anything else"). Optional, not
 * because a real document or SSE `questions` entry is ever missing it — it
 * never is — but because dozens of pre-existing `KeyQuestionDoc` literals
 * across this feature's own test suite predate this field and never set
 * it; requiring it would force an unrelated edit onto every one of them.
 * `compareKeyQuestionOrder` below treats an absent value as "sorts last,
 * stably" so those fixtures keep rendering in their own array order rather
 * than jumping unpredictably.
 */
export interface KeyQuestionDoc {
  id: string;
  question: string;
  subquestions?: string[];
  order?: number;
  /** Backend-owned chapter order. Zero is a valid first position. */
  readOrder?: number;
  component?: string;
  catalogEngagements?: string[];
  matchedFromUserQuestion?: string;
  source?: string;
}

/**
 * The one comparator every render/merge path uses to sequence key
 * questions — never a lexical `id` compare (that was the bug this
 * comparator replaces: `id` is an opaque identifier, not a position, and
 * sorting by it can place a question before the ones it is meant to build
 * on). `Array.prototype.sort` is a stable sort (ES2019+), so two entries
 * that both lack `order` (or share a value) keep their relative input
 * order rather than being reshuffled — this is what lets test fixtures
 * that predate the `order` field keep passing unchanged.
 *
 * Exported from this types module (not `advisoryPresentation.ts`, which
 * lives under `pages/Advisory/` and is not importable from the redux slice
 * layer) so both `advisorySlice.ts` (the Firestore/poll merge path) and
 * `AdvisoryKeyQuestionEditor.tsx` (the render path, covering the SSE path
 * too) share the exact same rule rather than risking two definitions that
 * drift.
 */
export function compareKeyQuestionOrder(a: { order?: number }, b: { order?: number }): number {
  const orderOf = (q: { order?: number }): number =>
    typeof q.order === 'number' ? q.order : Number.MAX_SAFE_INTEGER;
  return orderOf(a) - orderOf(b);
}

/**
 * One row of the engagements-list channel (`AdvisoryListenerKind:
 * 'engagements'`).
 *
 * Carries `goal`, `maxIterations` and the derived `canRefine` — the same
 * three values `StrategicAdvisoryDoc` carries, by the same extraction and
 * the same derivation — so a list row can be padded into a
 * `StrategicAdvisoryDoc`-shaped object and fed to the UNCHANGED
 * `deriveAdvisoryStage`, instead of a parallel list-only status derivation
 * drifting from the detail route's own. This costs no extra Firestore
 * reads: the engagements query already reads
 * `users/{uid}/strategic_advisory/{projectId}` documents directly, not a
 * denormalised summary copy.
 */
export interface EngagementSummaryDoc {
  id: string;
  originalQuery?: string;
  status: GoalDocStatus;
  title: string | null;
  updatedAt: string | null;
  goal: GoalAttempt[];
  maxIterations: number;
  canRefine: boolean;
}

/**
 * The three advisory listener kinds. There is no `criteria` kind — no
 * backend `criteria` subcollection has ever existed for one to name.
 */
export type AdvisoryListenerKind = 'engagements' | 'goal' | 'keyQuestions';

/**
 * Per-channel health, reusable across all three listener kinds:
 * `deriveChannelState` is generic over every listener kind including
 * `engagements`, while `deriveAdvisoryStage` stays narrow over the goal
 * channel. This keeps loading/error state scoped per channel — no global
 * `loading` or `error` field may exist anywhere the advisory slice consumes
 * this type, since that would leak across concurrently-listened items.
 */
export type ChannelState = 'loading' | 'ready' | 'empty' | 'denied' | 'failed';

/**
 * Structural status of one advisory channel.
 *
 * `errorCode` is deliberately an error CODE ONLY, never a provider message
 * string. A raw Firestore error message (`FirebaseError.message`) must
 * never reach any state a component can render — the code is the only
 * thing allowed to cross this boundary, and `advisoryErrorCodes.ts` turns
 * it into an i18n key. This type therefore intentionally has no `message`
 * field.
 */
export interface AdvisoryChannelStatus {
  firstSnapshotReceived: boolean;
  isEmpty: boolean;
  errorCode: string | null;
}

/**
 * Server-state-derived stage of an advisory engagement. A discriminated
 * union whose variants carry their required payload — e.g.
 * `awaitingConfirmation` carries `attempt`, `maxIterations` and
 * `canRefine`, so no component can hardcode an iteration cap.
 *
 * Every tag below is deliberately distinct: one generic error tag must
 * never stand in for two different situations. `denied` (channel read was
 * denied) is never conflated with `channelFailed` (listener/mapper failure
 * that is not a denial), and neither is conflated with `extractionFailed`
 * (the goal doc's own `status === 'failed'`).
 *
 * Deliberately NOT included: stages like `confirmed_choosing_path`,
 * `customizing` and `document_upload` that depend on a client-only fork
 * overlay not yet built. This stage function stays narrow over the goal
 * channel.
 */
export type AdvisoryStage =
  /** The channel has not delivered a first snapshot yet. */
  | { tag: 'loading' }
  /** The channel read was denied. Distinct from every failure tag. */
  | { tag: 'denied' }
  /** Listener or mapper failure that is not a denial. */
  | { tag: 'channelFailed'; errorCode: string }
  /** The channel is healthy and the goal document does not exist. */
  | { tag: 'absent' }
  | { tag: 'extracting' }
  /** A unified draft is being prepared, edited, or approved. */
  | { tag: 'reviewing' }
  /**
   * The company was read from the user's question and has not been answered
   * yet. Ordered BEFORE the goal branches by `deriveAdvisoryStage`, because the
   * subject is what the engagement type, the question set and every figure
   * downstream are all built on — correcting it after the plan exists means
   * discarding the plan.
   *
   * `extractionFailed` here is the model's failure, not the user's dead end:
   * the screen still asks, and the user types the name.
   */
  | { tag: 'awaitingCompanyConfirmation'; companyName: string; extractionFailed: boolean }
  /**
   * The engagement is classified as one of the two per-counterparty types and
   * the list has not been settled. Ordered AFTER the company gate and BEFORE
   * the fork, because the engagement type is what makes a counterparty
   * meaningful and the question set is what it gates.
   */
  | {
      tag: 'awaitingCounterpartyConfirmation';
      companies: string[];
      kind: string;
      extractionFailed: boolean;
    }
  /** Carries everything the refine UI needs — no component may hardcode 3 or 5. */
  | { tag: 'awaitingConfirmation'; attempt: number; maxIterations: number; canRefine: true }
  | { tag: 'refinementExhausted'; attempt: number; maxIterations: number }
  | { tag: 'confirmed' }
  /** The goal doc's own `status === 'failed'`. Distinct from `denied` and `channelFailed`. */
  | { tag: 'extractionFailed' }
  /**
   * The V3 conversation exists and its docintel handles are published, but
   * nothing is running. This is the document-upload window, and it is a stage of
   * its own precisely because it is the ONLY point at which an upload still
   * reaches the engagement — a node reads its collection as it stands when its
   * turn runs.
   */
  /**
   * The counterparties are confirmed and their profiles are being researched,
   * or have finished with something the user could still act on.
   *
   * Ordered AFTER the counterparty gate and BEFORE the fork: the research fires
   * on the confirmation and the analysis waits for it, so this is where the
   * user sees it happen rather than watching a silent wizard.
   */
  | {
      tag: 'researchingCounterparties';
      kind: string;
      rows: CounterpartyProfileRow[];
      /** True while any company is still in flight. */
      pending: boolean;
      /**
       * Companies given up on. Carried on the stage rather than looked up by the
       * screen because this stage is the only place the user is told — and a
       * drop is the one outcome here they cannot act on.
       */
      dropped: DroppedCounterparty[];
    }
  | { tag: 'analysisPrepared'; docsCollectionId: string }
  /**
   * A run is in flight. Carries its own progress counters so no component
   * hardcodes a turn count, the same rule `awaitingConfirmation` follows for the
   * iteration cap.
   */
  | {
      tag: 'analysisRunning';
      runId: string;
      plannedTurns: number;
      completedTurns: number;
      failedTurns: number;
    }
  /** Every planned turn completed. */
  | { tag: 'analysisComplete'; runId: string; completedTurns: number; plannedTurns: number }
  /**
   * Terminal, and deliberately NOT `analysisFailed`: a run where one branch died
   * still answered the others. Collapsing the two would throw away work the user
   * paid for, which is why agent-svc reports them as different statuses.
   */
  | {
      tag: 'analysisPartial';
      runId: string;
      completedTurns: number;
      failedTurns: number;
      plannedTurns: number;
    }
  /**
   * The run failed. Distinct from `extractionFailed` (the goal doc's own
   * failure), from `channelFailed` (a listener problem) and from `denied` — one
   * generic error tag must never stand in for four different situations.
   */
  | { tag: 'analysisFailed'; runId: string };

/**
 * One row of the profile-research screen: a confirmed counterparty and whatever
 * is known about its profile so far. `status` is `null` when no record exists —
 * the run has not reached that company yet.
 */
export interface CounterpartyProfileRow {
  company: string;
  status: CounterpartyProfileStatus | null;
  fileName: string;
  chars: number;
  detail: string;
  sourceUrl: string;
}

/** The literal-union of every valid `AdvisoryStage` tag. */
export type AdvisoryStageTag = AdvisoryStage['tag'];
